/// <reference lib="dom" />
/// <reference lib="dom.iterable" />
// TypeScript ships no IndexedDB-only lib, so the DOM lib rides this module —
// the one every browser-storage sibling in this directory imports. The rest of
// the package compiles platform-free (see ../locks.ts).

import { FieldSyncQuotaError, type FieldSyncQuotaOperation } from "../errors";

// The per-profile IndexedDB database behind the browser storage adapter: one
// database per signed-in profile, so a shared machine never mixes testers'
// queued regulatory work and a profile's queue drains only under its owner's
// session.
//
// FIFO without a created_at index: the device store orders its outbox reads by
// client_submission_id ASC, and those keys are UUIDv7 — time-ordered by
// construction — so an IndexedDB cursor over the primary key walks the same
// order. nextPending-class reads walk the key cursor and take the first row
// matching the state predicate rather than loading the whole store.

interface StoreDefinition {
  name: string;
  keyPath: string;
  indexes: readonly string[];
}

// The twenty-two device-store tables by name and key, plus `evidence`: on the
// device staged evidence is a directory tree of files, here it is a Blob
// store, keyed and indexed like the attachment rows that reference it.
const STORES: readonly StoreDefinition[] = [
  { name: "bundle_assignments", keyPath: "id", indexes: [] },
  { name: "bundle_template_versions", keyPath: "id", indexes: [] },
  { name: "bundle_targets", keyPath: "target_key", indexes: [] },
  { name: "bundle_kits", keyPath: "id", indexes: [] },
  { name: "bundle_supplements", keyPath: "supplement_key", indexes: ["company_id"] },
  { name: "bundle_facilities", keyPath: "facility_id", indexes: [] },
  { name: "bundle_walkup_templates", keyPath: "template_id", indexes: ["org_id"] },
  { name: "bundle_requirements", keyPath: "org_id", indexes: [] },
  { name: "bundle_category_templates", keyPath: "template_key", indexes: [] },
  { name: "bundle_change_request_sets", keyPath: "id", indexes: [] },
  { name: "bundle_catalog_versions", keyPath: "org_id", indexes: [] },
  { name: "catalogs", keyPath: "org_id", indexes: [] },
  { name: "sync_meta", keyPath: "key", indexes: [] },
  { name: "drafts", keyPath: "assignment_id", indexes: [] },
  { name: "draft_attachments", keyPath: "client_attachment_id", indexes: ["assignment_id"] },
  { name: "draft_sidecars", keyPath: "key", indexes: [] },
  {
    name: "outbox_submissions",
    keyPath: "client_submission_id",
    indexes: ["state", "assignment_id"],
  },
  {
    name: "outbox_attachments",
    keyPath: "client_attachment_id",
    indexes: ["client_submission_id", "client_set_id"],
  },
  { name: "outbox_transitions", keyPath: "assignment_id", indexes: ["state"] },
  { name: "outbox_supplements", keyPath: "client_submission_id", indexes: ["state"] },
  { name: "outbox_assignments", keyPath: "assignment_id", indexes: ["state"] },
  { name: "outbox_change_request_sets", keyPath: "client_set_id", indexes: [] },
  { name: "evidence", keyPath: "client_attachment_id", indexes: ["assignment_id"] },
];

const DB_VERSION = 3;

/**
 * Opens `njbsoft-fieldwork-{profileId}` and brings it up to the current store
 * set. The upgrade is idempotent per store and per index rather than a
 * first-open creation sweep: a device that already holds a queue opens at an
 * older version, and creating a store that exists throws — so every store the
 * version bump did not add must be stepped over, never recreated, and never
 * dropped (the queue on it is regulatory work). The `factory` argument is what
 * lets a test hand in `fake-indexeddb` without patching a global.
 */
export function openFieldworkDb(
  profileId: string,
  factory: IDBFactory = indexedDB,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(`njbsoft-fieldwork-${profileId}`, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      // The versionchange transaction, the only handle that can reach an
      // ALREADY-EXISTING store to add a missing index.
      const upgrade = request.transaction;
      if (upgrade === null) {
        reject(new Error("indexeddb_upgrade_without_transaction"));
        return;
      }
      for (const definition of STORES) {
        const store = db.objectStoreNames.contains(definition.name)
          ? upgrade.objectStore(definition.name)
          : db.createObjectStore(definition.name, { keyPath: definition.keyPath });
        for (const index of definition.indexes) {
          if (!store.indexNames.contains(index)) store.createIndex(index, index);
        }
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb_open_failed"));
    };
  });
}

/** Resolves with the request's result; rejects with its error. */
export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb_request_failed"));
    };
  });
}

/** Resolves when the transaction commits; rejects with its error on abort. */
export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error("indexeddb_transaction_aborted"));
    };
  });
}

// Both getAll sources (a store, an index) narrow their DOM-typed any[] result
// to the row shape the caller names — every row in these stores was written by
// the group factory that owns them, with that shape.
export function getAllRows<T>(
  source: { getAll(query?: IDBValidKey): IDBRequest<T[]> },
  query?: IDBValidKey,
): Promise<T[]> {
  return requestToPromise(source.getAll(query));
}

export async function getRow<T>(
  source: { get(query: IDBValidKey): IDBRequest<T | undefined> },
  key: IDBValidKey,
): Promise<T | null> {
  const row = await requestToPromise(source.get(key));
  return row ?? null;
}

/**
 * Mutates the row at `key` inside one readwrite transaction — the single-row
 * UPDATE twin. A missing row is a no-op (an UPDATE matching zero rows);
 * `mutate` returns whether the row is written back, carrying the statement's
 * extra WHERE conditions.
 */
export async function updateRow<T>(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey,
  mutate: (row: T) => boolean,
): Promise<void> {
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  const row = await getRow<T>(store, key);
  if (row !== null && mutate(row)) {
    store.put(row);
  }
  await txDone(tx);
}

// The device store's ORDER BY on TEXT columns compares bytewise; code-unit
// comparison is the in-memory twin.
export function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// Cursor-walks `store` deleting every row the predicate matches — the
// multi-row `DELETE … WHERE` twin, running inside the caller's transaction.
export function deleteRowsWhere<T>(
  store: IDBObjectStore,
  predicate: (row: T) => boolean,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor === null) {
        resolve();
        return;
      }
      const row: T = cursor.value;
      if (predicate(row)) {
        cursor.delete();
      }
      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb_request_failed"));
    };
  });
}

/**
 * Runs `fn` inside one readwrite transaction over `stores` and settles only
 * after the transaction does. A `QuotaExceededError`-named failure — thrown
 * from `fn` or carried by the abort — maps to `FieldSyncQuotaError` for the
 * named capture-path operation, so a full disk surfaces as the loud, retriable
 * failure it is; every other failure aborts the transaction and propagates as
 * itself.
 */
export async function runWrite<T>(
  db: IDBDatabase,
  stores: readonly string[],
  operation: FieldSyncQuotaOperation,
  fn: (tx: IDBTransaction) => Promise<T>,
): Promise<T> {
  const tx = db.transaction([...stores], "readwrite");
  const done = txDone(tx);
  try {
    const value = await fn(tx);
    await done;
    return value;
  } catch (err) {
    try {
      tx.abort();
    } catch {
      // The transaction already aborted (or finished) on its own.
    }
    await done.catch(() => undefined);
    if (isQuotaExceeded(err)) {
      throw new FieldSyncQuotaError(operation, err);
    }
    throw err;
  }
}

function isQuotaExceeded(err: unknown): boolean {
  return (
    typeof err === "object" && err !== null && "name" in err && err.name === "QuotaExceededError"
  );
}
